import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useForm, useWatch } from '@kanda-libs/ks-component-ts';

import { selectors } from 'store';
import partnerSelectors from 'store/selectors/partner';

export interface PartnersAnalyticsHook {
  form: ReturnType<typeof useForm>;
  showAnalytics: boolean;
  totalSubscriptions: number;
  totalActivations: number;
}

export default function usePartnersAnalytics(): PartnersAnalyticsHook {
  const form = useForm();
  const { id } = useSelector(selectors.getPathKey);
  const partners = useSelector(partnerSelectors.getFilteredPartners);
  const referrals = useSelector(partnerSelectors.getFilteredReferrals);

  const [filterDate] = useWatch({
    name: ['filterDate'],
    control: form.control,
  });

  const date = useMemo(
    () => new Date((filterDate || '').split('/').reverse().join('-')),
    [filterDate],
  );

  const validDate = useMemo(() => !isNaN(date.getTime()), [date]);

  const filteredReferrals = useMemo(() => {
    if (!validDate) return referrals;
    return referrals.filter((referral) => {
      if (!referral.metadata) return false;
      return new Date(referral.metadata.created_at) >= date;
    });
  }, [referrals, date, validDate]);

  const totalSubscriptions = useMemo(
    () =>
      filteredReferrals.reduce((acc, referral) => {
        if (referral.billing === 'subscribed') return acc + 1;
        return acc;
      }, 0),
    [filteredReferrals, id],
  );

  const totalActivations = useMemo(
    () =>
      filteredReferrals.reduce((acc, referral) => {
        if (referral.lifecycle === 'verified') return acc + 1;
        return acc;
      }, 0),
    [filteredReferrals, id],
  );

  return {
    form,
    showAnalytics: !!id,
    totalSubscriptions,
    totalActivations,
  };
}
